'use client';

import { ReactNode, useContext, type FC } from 'react';
import styles from './PreviewQuestionItem.module.css';
import {
  Button,
  ButtonGroup,
  InlineUnsafe,
  Text,
  Flex,
  Pill,
  Icon,
} from '@/_components';
import { PreviewQuestionItemProps } from './PreviewQuestionItem.types';
import {
  PREDEFINED_MARKED_COMPLETED,
  QUESTION_SEPARATOR,
  PREDEFINED_NOT_APPLICABLE,
} from '@/_constants/questions';
import { getClasses } from '@/_utils/classnames';
import { PreviewContext } from '@/_contexts/PreviewProvider';
import { getUploadedItems } from '@/_utils/answers';
import { getRealDocumentPath } from '@/_utils/documents';
import { UserContext } from '@/_contexts/UserProvider';
import { isSorabanEmail } from '@/_utils/admin';
import { getHasPermission } from '@/_utils/permissions';
import { PermissionsMapEnum } from '@/_types/permissions';

export const PreviewQuestionItem: FC<PreviewQuestionItemProps> = ({
  isDeleteLoading,
  isHighlighted = false,
  isMarkLoading,
  onDelete,
  onMarkCompleted,
  onMarkUndo,
  onNotesToggle,
  question,
  questionLevel,
  questionnaire,
}) => {
  const { setPreviewFile } = useContext(PreviewContext);
  const { token, permissions, user } = useContext(UserContext);

  const markCompleted = () => onMarkCompleted(question);
  const markUndo = () => onMarkUndo(question);
  const notesToggle = () => onNotesToggle(question);

  const noteCount: number =
    question?.filtered_questionnaire_comments?.length ?? 0;
  const isMarkedCompleted: boolean =
    question.answer === PREDEFINED_MARKED_COMPLETED;

  const answerBlankOrMarkedCompleted = !question.answer || isMarkedCompleted;

  const isSorabanUser = isSorabanEmail(user?.email, user?.is_advisor);

  const previewQuestionItemClasses: string = getClasses(
    styles.previewQuestionItem,
    {
      [styles.previewQuestionItemL1]: questionLevel === 1,
      [styles.previewQuestionItemL2]: questionLevel === 2,
      [styles.previewQuestionItemL3]: questionLevel === 3,
      [styles.previewQuestionItemHighlighted]: isHighlighted,
    },
  );

  const hasEditQuestionnairePermission: boolean = getHasPermission(
    PermissionsMapEnum['questionnaire.edit'],
    permissions,
  );

  const getAnswer = (): ReactNode => {
    const isMarkedCompleted: boolean =
      question.answer === PREDEFINED_MARKED_COMPLETED;

    if (!question.answer && !question.answer_by) return null;
    if (isMarkedCompleted) return null;

    if (question.answer === PREDEFINED_NOT_APPLICABLE) {
      return (
        <Flex isColumn={true} gap={4}>
          <Pill
            size="large"
            label="Client marked not applicable"
            intent="secondary"
          />
        </Flex>
      );
    }

    if (question.answer_by) {
      return (
        <Flex isColumn={true} gap={4}>
          <Pill
            size="large"
            label={`Client will ${question.question_type === 'upload' ? 'upload' : 'answer'} by: ${question.answer_by}`}
            intent="secondary"
          />
        </Flex>
      );
    }

    if (question.question_type === 'upload') {
      const files: Array<string> = getUploadedItems(question.answer);

      return (
        <Flex isColumn={true} gap={4}>
          <Text type="small">Uploaded:</Text>
          {files?.map((file, key) => {
            const onPreview = async () => {
              const realDocumentUrl: string | undefined =
                await getRealDocumentPath(questionnaire, file, token!);

              setPreviewFile(realDocumentUrl);
            };

            return (
              <Flex gap={2} key={key} isColumn={true}>
                <Text type="small" isBreakAll={true}>
                  {file}
                </Text>
                <ButtonGroup>
                  <Button
                    intent="secondary"
                    value="Preview"
                    size="tiny"
                    iconLeft="eye"
                    onClick={onPreview}
                  />
                </ButtonGroup>
              </Flex>
            );
          })}
        </Flex>
      );
    }

    if (question.answer?.includes(QUESTION_SEPARATOR)) {
      const items: Array<string> = getUploadedItems(question.answer);

      return (
        <Flex isColumn={true} gap={2}>
          {items.map((item, key) => {
            return (
              <Flex isYCentered={true} gap={2} key={key}>
                <Icon name="dot" size={20} />
                <Text type="small">
                  <InlineUnsafe text={item} />
                </Text>
              </Flex>
            );
          })}
        </Flex>
      );
    }

    return <i>{question.answer}</i>;
  };

  return (
    <Flex
      isBetween={true}
      gap={4}
      className={previewQuestionItemClasses}
      id={`question-${question.id}`}
    >
      <Flex gap={4} isYCentered={true}>
        {questionLevel > 1 && (
          <Icon name="arrow-bend-down-right" size={16} color="disabled" />
        )}
        <Flex isColumn={true}>
          <Text type="div" fontWeight="semi-bold" data-test-id="QUESTION.TEXT">
            <InlineUnsafe isExtremelyUnsafe={true} text={question.text} />
            {getAnswer()}
          </Text>

          {isMarkedCompleted && (
            <Flex isInline={true}>
              <Pill
                size="small"
                label={PREDEFINED_MARKED_COMPLETED}
                intent="secondary"
              />
            </Flex>
          )}
        </Flex>
      </Flex>
      <ButtonGroup isSameLine={true} isSmallGroup={true}>
        <Button
          onClick={notesToggle}
          value={`${noteCount}`}
          iconLeft="note"
          size="tiny"
          intent="secondary"
        />
        {(answerBlankOrMarkedCompleted || isSorabanUser) && (
          <Button
            onClick={isMarkedCompleted ? markUndo : markCompleted}
            iconLeft="check"
            size="tiny"
            isLoading={isMarkLoading}
            isActive={isMarkedCompleted}
            isDisabled={!hasEditQuestionnairePermission}
            isIconOnly={true}
            withTooltip={true}
            value={
              isMarkedCompleted ? 'Undo Marked Completed' : 'Mark Completed'
            }
            intent="secondary"
          />
        )}
        <Button
          onClick={onDelete}
          iconLeft="trash"
          size="tiny"
          isIconOnly={true}
          isLoading={isDeleteLoading}
          withTooltip={true}
          isDisabled={!hasEditQuestionnairePermission}
          value="Delete question"
          intent="danger-secondary"
        />
      </ButtonGroup>
    </Flex>
  );
};
